<template>
  <div>
    <v-data-table
      :headers="currentHeaders"
      :items="passengers"
      sort-by="lastName"
      sort-desc
      :mobile-breakpoint="dataTableMobileBreakpoint"
      no-data-text="No passengers assigned to voyage"
      :item-class="rowClassForItem"
    >
      <template v-slot:top>
        <div class="d-flex pa-4">
          <span class="headline primary--text font-weight-medium">
            <v-icon class="primary--text" left>mdi-account-group</v-icon
            >Passengers
          </span>

          <v-spacer />

          <csv-download-button
            class="mx-2"
            :size="headerButtonSize"
            :data="passengerCsvModel"
            :fields="csvExportFields"
            :labels="csvExportLabels"
            :fileName="getExportFileName()"
          />
          <v-btn
            v-bind="headerButtonSize"
            color="primary"
            @click="openCreatePassengerForm"
          >
            <v-icon left>mdi-plus</v-icon>Add
          </v-btn>
        </div>
      </template>
      <template v-slot:item.syncState="{ item }">
        <SyncStateIcon
          v-if="anyUnsyncedPassengers"
          :isItemUnsynced="isPassengerUnsynced(item)"
        />
      </template>
      <template v-slot:[`item.pickupDateTimeLt`]="{ item }">
        <span>{{
          item.pickupDateTimeLt | removeTAndSecondsAndFormatDate
        }}</span>
      </template>
      <template v-slot:[`item.dropOffDateTimeLt`]="{ item }">
        <span>{{
          item.dropOffDateTimeLt | removeTAndSecondsAndFormatDate
        }}</span>
      </template>
      <!-- status icon -->
      <template v-slot:[`item.status`]="{ item }">
        <PassengerStatusIcon :status="item.status" />
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <div class="d-flex">
          <v-icon
            :class="item.status === 'VoyageComplete' && 'hidden'"
            :size="iconSize"
            @click="openEditDialog(item, true)"
            >mdi-calendar-edit</v-icon
          >
          <v-icon :size="iconSize" @click="openEditDialog(item)" class="mx-2"
            >mdi-pencil</v-icon
          >
          <v-icon
            :size="iconSize"
            :disabled="!isClientConnectedToServer && !item.idc"
            @click="openDeleteDialog(item)"
            >mdi-delete</v-icon
          >
        </div>
      </template>
    </v-data-table>

    <!-- Form Dialog -->
    <v-dialog v-model="formDialog" max-width="950px">
      <v-card>
        <PassengerForm
          ref="passengerForm"
          :passenger="passenger"
          :onSubmitClick="submitPassenger"
          :onCloseButtonClick="closeFormDialog"
          :isLoading="isSubmitting"
          :isEdit="isPassengerEdit"
          :isStatusUpdate="isStatusUpdate"
        />
      </v-card>
    </v-dialog>

    <!-- Confirm Dialog -->
    <ConfirmDialog
      v-model="showDeleteDialog"
      title="Delete Passenger"
      description="Are you sure you want to delete this passenger?"
      :onConfirmClick="deletePassenger"
      :onCloseClick="closeDeleteDialog"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import dateTimeHelpers from '../../mixins/DateTimeHelpers';
import ResponsiveSizeHelperMixin from '../../mixins/ResponsiveSizeHelperMixin';
import PassengerForm from './PassengerForm.vue';
import ConfirmDialog from '../ui/ConfirmDialog.vue';
import PassengerStatusIcon from './PassengerStatusIcon.vue';
import SyncStateIcon from '../ui/SyncStateIcon.vue';
import CsvDownloadButton from '../ui/CsvDownloadButton.vue';

export default {
  name: 'Passengers',
  mixins: [ResponsiveSizeHelperMixin],
  components: {
    PassengerForm,
    ConfirmDialog,
    PassengerStatusIcon,
    SyncStateIcon,
    CsvDownloadButton,
  },
  props: {
    voyageReport: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      passenger: {},
      tableHeaders: [
        { text: 'Passenger No.', value: 'passengerNo' },
        { text: 'First Name', value: 'firstName' },
        { text: 'Last Name', value: 'lastName' },
        { text: 'Pickup Location', value: 'pickupLocation.name' },
        { text: 'Pickup Date (LT)', value: 'pickupDateTimeLt' },
        { text: 'DropOff Location', value: 'dropOffLocation.name' },
        { text: 'Drop Off Date (LT)', value: 'dropOffDateTimeLt' },
        { text: 'Status', value: 'status' },
        { text: '', value: 'actions' },
      ],
      syncStateColumn: { text: 'Sync State', value: 'syncState' },
      formDialog: false,
      isSubmitting: false,
      showDeleteDialog: false,
      editIndex: -1,
      isStatusUpdate: false,
      csvExportFields: [
        'passengerNo',
        'firstName',
        'lastName',
        'pickupLocationName',
        'pickupDateTimeLt',
        'dropOffLocationName',
        'dropOffDateTimeLt',
        'status',
        'voyageReportNo',
      ],
      csvExportLabels: {
        passengerNo: 'Passenger No.',
        firstName: 'First Name',
        lastName: 'Last Name',
        pickupLocationName: 'Pickup Location',
        pickupDateTimeLt: 'Pickup Date (LT)',
        dropOffLocationName: 'Drop Off Location',
        dropOffDateTimeLt: 'Drop Off Date (LT)',
        status: 'Status',
        voyageReportNo: 'Voyage Report',
      },
    };
  },
  watch: {
    formDialog(newVal) {
      if (newVal === false) {
        this.closeFormDialog();
      }
    },
  },
  computed: {
    ...mapGetters({
      isClientConnectedToServer: 'userStore/isClientConnectedToServer',
      getPassengersForVoyage: 'passengersStore/getSyncedAndUnsyncedPassengersForVoyage',
    }),
    passengers() {
      const voyageReportId = this.voyageReport.id;
      const voyageReportIdc = this.voyageReport.idc;
      return this.getPassengersForVoyage(voyageReportId ?? voyageReportIdc);
    },
    isPassengerEdit() {
      return this.editIndex > -1 && !this.isStatusUpdate;
    },
    anyUnsyncedPassengers() {
      return this.passengers.some((x) => (x && x.idc));
    },
    currentHeaders() {
      let headers = this.tableHeaders;
      if (this.anyUnsyncedPassengers) {
        headers = [
          this.syncStateColumn,
          ...this.tableHeaders,
        ];
      }
      return headers;
    },
    passengerCsvModel() {
      return this.passengers.map((x) => (
        {
          ...x,
          pickupLocationName: x.pickupLocation ? x.pickupLocation.name : '',
          dropOffLocationName: x.dropOffLocation ? x.dropOffLocation.name : '',
          voyageReportNo: x.voyageReport.voyageNo,
        }
      ));
    },
  },
  methods: {
    openCreatePassengerForm() {
      this.isStatusUpdate = false;
      this.passenger = {};
      this.editIndex = -1;

      this.formDialog = true;
    },
    closeFormDialog() {
      this.formDialog = false;
      this.$nextTick(() => {
        const passengerFormRef = this.$refs.passengerForm;
        if (passengerFormRef) passengerFormRef.reset();
      });
    },
    openDeleteDialog(passenger) {
      this.passenger = { ...passenger };
      this.showDeleteDialog = true;
    },
    closeDeleteDialog() {
      this.passenger = {};
      this.showDeleteDialog = false;
    },
    submitPassenger() {
      this.isSubmitting = true;

      if (this.editIndex <= -1) {
        this.createPassenger();
      } else {
        this.updatePassenger();
      }
    },
    createPassenger() {
      const newPassenger = this.getPassengerModel(this.passenger);

      this.$store.dispatch('passengersStore/createPassenger', newPassenger)
        .then((resp) => {
          this.onSuccess(resp.message);
          this.passenger = {};
          this.$refs.passengerForm.reset();
        })
        .catch((err) => { this.onError(err.message, err.data); })
        .finally(() => { this.isSubmitting = false; });
    },
    updatePassenger() {
      const updatedPassengerModel = this.getPassengerModel(this.passenger);

      this.$store.dispatch('passengersStore/updatePassenger', updatedPassengerModel)
        .then((resp) => {
          this.onSuccess(resp.message);
          this.closeFormDialog();
        })
        .catch((err) => { this.onError(err.message, err.data); })
        .finally(() => { this.isSubmitting = false; });
    },
    deletePassenger() {
      return this.$store.dispatch('passengersStore/deletePassenger', this.passenger)
        .then((result) => this.onSuccess(result.message, result))
        .then(() => this.closeDeleteDialog())
        .catch((err) => this.onError(err.message, err.data));
    },
    openEditDialog(passenger, statusUpdate = false) {
      this.editIndex = this.passengers.indexOf(passenger);
      this.isStatusUpdate = statusUpdate;
      this.passenger = { ...passenger };

      // split date and time to individual properties for date and time pickers
      const { pickupDateTimeLt, dropOffDateTimeLt } = this.passenger;
      if (pickupDateTimeLt) {
        this.passenger.pickupDate = dateTimeHelpers
          .getDateStringFromDateTime(pickupDateTimeLt);
        this.passenger.pickupTime = dateTimeHelpers
          .getTimeStringFromDateTime(pickupDateTimeLt);
      }

      if (dropOffDateTimeLt) {
        this.passenger.dropOffDate = dateTimeHelpers
          .getDateStringFromDateTime(dropOffDateTimeLt);
        this.passenger.dropOffTime = dateTimeHelpers
          .getTimeStringFromDateTime(dropOffDateTimeLt);
      }

      this.formDialog = true;
    },
    getPassengerModel(passenger) {
      // Location Ids
      const pickupLocationId = passenger.pickupLocation
        ? passenger.pickupLocation.id
        : null;

      const dropOffLocationId = passenger.dropOffLocation
        ? passenger.dropOffLocation.id
        : null;

      const pickupDateTimeLt = dateTimeHelpers
        .dateAndTimeLtString(passenger.pickupDate, passenger.pickupTime);

      const dropOffDateTimeLt = dateTimeHelpers
        .dateAndTimeLtString(passenger.dropOffDate, passenger.dropOffTime);

      return {
        ...passenger,
        pickupLocationId,
        dropOffLocationId,
        pickupDateTimeLt,
        dropOffDateTimeLt,
        voyageReport: this.voyageReport,
        voyageReportId: this.voyageReport.id ?? undefined,
        voyageReportIdc: this.voyageReport.idc,
      };
    },
    isPassengerUnsynced(passenger) {
      const hasIdc = !!passenger.idc;
      return hasIdc;
    },
    rowClassForItem(item) {
      let rowClass = '';
      if ((this.anyUnsyncedPassengers) && item) {
        rowClass = (this.isPassengerUnsynced(item)) ? 'green lighten-4' : 'blue lighten-4';
      }

      return rowClass;
    },
    getExportFileName() {
      const voyageNo = this.voyageReport ? this.voyageReport.voyageNo : '';
      const dateTime = dateTimeHelpers.getFileTimestampNow();
      return `VoyageNo-${voyageNo}_Passengers-${dateTime}.csv`;
    },
    onSuccess(message, data) {
      this.$toast.success(message);
      this.closeFormDialog();
    },
    onError(message, data) {
      this.$toast.error(message);
    },
  },
  created() {
    this.$store.dispatch('passengersStore/loadPassengersForVoyage', this.voyageReport.id);
  },
  beforeDestroy() {
    this.$store.dispatch('passengersStore/clearPassengers');
  },
};
</script>
